// Version history for one persona inside the edit drawer (§4a Drawer: "row
// detail + history"). Reads the append-only audit ledger filtered to this
// persona — who changed what, when — and labels each entry from its
// before/after diff (spec saves carry the minted revision number; a
// display-name PATCH renders as a rename). Role note: owner/admin see the
// full trail; member/viewer are server-filtered to their own actions.
import type { components } from "../../api/schema";
import { formatDateTime } from "../../lib/format";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery } from "../../lib/useListQuery";
import {
  Badge,
  Button,
  Inline,
  InlineError,
  RouterLink,
  ShellIcon,
  Skeleton,
  Stack,
  Text,
} from "../../ui";
import { isRecord } from "../agent/lib/connectionConfig";
import {
  uniqueIds,
  userMap,
  userOptionLabel,
  UserLabel,
  useUserDirectoryQuery,
} from "../shared/userDirectory";

type AuditLogResponse = components["schemas"]["AuditLogResponse"];

export function PersonaHistory({ personaId }: { personaId: string }) {
  const entries = useListQuery("/v1/audit-logs", {
    params: { resource_id: personaId, resource_type: "persona" },
    queryKey: queryKeys.auditLogs.list({
      resource_id: personaId,
      surface: "persona-history",
    }),
  });

  const actorIds = uniqueIds(entries.items.map((entry) => entry.actor_id));
  const users = useUserDirectoryQuery(actorIds.join(","), "persona-history");
  const usersById = userMap(users.data?.items ?? []);

  return (
    <Stack gap={3}>
      <Inline align="center" gap={1}>
        <Text variant="caps">History</Text>
        <RouterLink
          aria-label="View Persona audit log"
          search={(current) => ({
            ...current,
            resource_id: personaId,
            resource_type: "persona",
          })}
          title="View Persona audit log"
          to="/settings/audit-log"
        >
          <ShellIcon name="arrowUpRight" />
        </RouterLink>
      </Inline>
      {entries.isPending ? (
        <Skeleton label="Loading history" rows={3} shape="table-row" />
      ) : entries.errorMessage !== undefined ? (
        <InlineError>{entries.errorMessage}</InlineError>
      ) : entries.items.length === 0 ? (
        <Text variant="muted">No recorded changes.</Text>
      ) : (
        <Stack gap={3}>
          {entries.items.map((entry) => {
            const row = historyEntry(entry);
            const actor =
              entry.actor_id === null
                ? undefined
                : usersById.get(entry.actor_id);
            return (
              <Inline align="start" justify="between" key={entry.id}>
                <Stack gap={1}>
                  <Inline align="center" gap={2}>
                    <Text>{row.label}</Text>
                    {row.revision !== null ? (
                      <Badge tone="neutral">
                        {`Revision ${String(row.revision)}`}
                      </Badge>
                    ) : null}
                  </Inline>
                  {row.fields.length > 0 ? (
                    <Text variant="muted">
                      {row.fields.length === 1
                        ? "Changed field: "
                        : "Changed fields: "}
                      {row.fields.join(", ")}
                    </Text>
                  ) : null}
                  {entry.actor_id === null ? (
                    <Text as="span" variant="muted">
                      System
                    </Text>
                  ) : actor === undefined ? (
                    <UserLabel fallbackId={entry.actor_id} />
                  ) : (
                    <Text as="span" variant="muted">
                      {userOptionLabel(actor)}
                    </Text>
                  )}
                </Stack>
                <Text as="span" variant="muted">
                  {formatDateTime(entry.occurred_at)}
                </Text>
              </Inline>
            );
          })}
          {entries.hasNextPage ? (
            <Inline>
              <Button
                disabled={entries.isFetchingNextPage}
                size="sm"
                variant="secondary"
                onClick={entries.fetchNextPage}
              >
                {entries.isFetchingNextPage ? "Loading..." : "Load more"}
              </Button>
            </Inline>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}

// One revision-list row: a human action label plus the minted revision (when
// the entry carries one), rendered as a Badge beside the label.
function historyEntry(entry: AuditLogResponse): {
  fields: string[];
  label: string;
  revision: number | null;
} {
  const diff = isRecord(entry.diff) ? entry.diff : null;
  const before = diff && isRecord(diff.before) ? diff.before : null;
  const after = diff && isRecord(diff.after) ? diff.after : null;

  if (entry.action === "create") {
    return { fields: [], label: "Created", revision: revisionNumber(after) };
  }
  if (entry.action === "delete") {
    return { fields: [], label: "Deleted", revision: null };
  }
  if (entry.action === "update") {
    const fields = changedPersonaFields(before, after);
    const beforeName = displayName(before);
    const afterName = displayName(after);
    if (beforeName !== null && afterName !== null && beforeName !== afterName) {
      return { fields, label: `Renamed to ${afterName}`, revision: null };
    }
    return { fields, label: "Saved", revision: revisionNumber(after) };
  }
  return { fields: [], label: entry.action, revision: null };
}

function changedPersonaFields(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): string[] {
  if (before === null || after === null) {
    return [];
  }
  const fields: string[] = [];
  if (displayName(before) !== displayName(after)) {
    fields.push("Display name");
  }

  const beforeSpec = personaSpec(before);
  const afterSpec = personaSpec(after);
  if (
    valueAt(beforeSpec, "llm_provider") !== valueAt(afterSpec, "llm_provider")
  ) {
    fields.push("LLM provider");
  }

  const beforeVoice = recordAt(beforeSpec, "voice_mapping");
  const afterVoice = recordAt(afterSpec, "voice_mapping");
  if (valueAt(beforeVoice, "provider") !== valueAt(afterVoice, "provider")) {
    fields.push("Voice provider");
  }
  if (selectedVoice(beforeVoice) !== selectedVoice(afterVoice)) {
    fields.push("Voice");
  }

  const beforePhone = recordAt(beforeSpec, "phone_endpoint");
  const afterPhone = recordAt(afterSpec, "phone_endpoint");
  if (valueAt(beforePhone, "mode") !== valueAt(afterPhone, "mode")) {
    fields.push("Number selection");
  }
  if (
    valueAt(beforePhone, "pool_number_ref") !==
    valueAt(afterPhone, "pool_number_ref")
  ) {
    fields.push("Phone number");
  }

  const beforeBehavior = recordAt(beforeSpec, "voice_behavior");
  const afterBehavior = recordAt(afterSpec, "voice_behavior");
  if (
    (valueAt(beforeBehavior, "first_speaker") ?? "agent") !==
    (valueAt(afterBehavior, "first_speaker") ?? "agent")
  ) {
    fields.push(
      `Who speaks first: ${afterBehavior?.first_speaker === "persona" ? "Persona" : "Agent"}`,
    );
  }
  if (
    JSON.stringify(valueAt(beforeSpec, "voice_behavior")) !==
    JSON.stringify(valueAt(afterSpec, "voice_behavior"))
  ) {
    fields.push("Voice behavior");
  }
  return fields.length > 0 ? fields : ["Persona settings"];
}

function personaSpec(
  record: Record<string, unknown>,
): Record<string, unknown> | null {
  return recordAt(recordAt(record, "current_revision"), "spec");
}

function recordAt(
  record: Record<string, unknown> | null,
  key: string,
): Record<string, unknown> | null {
  const value = record?.[key];
  return isRecord(value) ? value : null;
}

function valueAt(record: Record<string, unknown> | null, key: string): unknown {
  return record?.[key] ?? null;
}

function selectedVoice(mapping: Record<string, unknown> | null): unknown {
  const provider = valueAt(mapping, "provider");
  return typeof provider === "string" ? valueAt(mapping, provider) : null;
}

function revisionNumber(record: Record<string, unknown> | null): number | null {
  const revision =
    record && isRecord(record.current_revision)
      ? record.current_revision.revision
      : null;
  return typeof revision === "number" ? revision : null;
}

function displayName(record: Record<string, unknown> | null): string | null {
  const value = record?.display_name;
  return typeof value === "string" ? value : null;
}
